import _ from 'lodash';
import Autosuggest from 'react-autosuggest';
import Highlighter from 'react-highlight-words';
import React, { Component } from 'react';
import toastr from '../../../toastr';

import { Api, IApiResponse } from '../../typings/Api';

interface IVendorLookupRecord {
  readonly Address: string;
  readonly City: string;
  readonly Name: string;
  readonly Phone: string;
  readonly State: string;
  readonly Zipcode: string;
  readonly active: string;
  readonly itemid: string;
}

interface IProps {
  disabled: boolean;
  id: string;
  onVendorLoad: (vendorID: string) => void;
  overflowBreakout?: boolean;
  placeholder?: string;
  showAdditionalNames: boolean;
  showVendorName?: boolean;
  style: any;
}

interface IState {
  value: string;
  suggestions: IVendorLookupRecord[];
}

class VendorLookup extends Component<IProps, IState> {
  id: string;

  lastLookup: string | null = null;
  overflowBreakout = false;

  state = {
    suggestions: [],
    value: '',
  };

  constructor(props: IProps) {
    super(props);
    this.id = props.id;

    if (props.overflowBreakout) {
      this.overflowBreakout = true;
    }
  }

  set value(value: string) {
    this.setState({
      value,
    });
  }

  getSuggestions = async (value: string) => {
    // remove leading ? front counter people are customed to typing
    value = value[0] === '?' ? value.slice(1) : value;

    let res: IApiResponse;

    try {
      res = await Api.get('/list/AAP13', {
        params: {
          limit: 100,
          q: `${value.trim().replace(/\//, '\\/').replace(/&/, '')}*`,
          skipAttachments: true,
        },
      });

      if (res.status === 200) {
        this.setState({
          suggestions: res.data.list.filter((x: IVendorLookupRecord) => x.active === 'Y'),
        });
      } else {
        throw new Error('200 not received from /list/AAP13');
      }
    } catch (err: any) {
      console.error(err.message);
      toastr.error('Error', 'Vendor search temporarily unavailable.');
      this.setState({
        suggestions: [],
      });
    }
  };

  getSuggestionsDebounced = _.debounce(this.getSuggestions, 250); // tslint:disable-line

  getSuggestionValue = (suggestion: IVendorLookupRecord) =>
    this.props.showVendorName ? `${suggestion.itemid}: ${suggestion.Name}` : suggestion.itemid;

  // Supplemental Data is used for additional pieces of data in a column such as Name2/Name3
  highlight = (query: string, textToHighlight: string, ...supplementalTextToHighlight: string[]): JSX.Element => {
    let result;
    if (!textToHighlight) {
      result = <div />;
    } else {
      result = (
        <div>
          <Highlighter
            autoEscape={true}
            highlightClassName="hl"
            searchWords={[query]}
            textToHighlight={textToHighlight}
          />
          {supplementalTextToHighlight.map((text, i) => (
            <React.Fragment key={`supTxt-${i}`}>
              <br />
              <Highlighter autoEscape={true} highlightClassName="hl" searchWords={[query]} textToHighlight={text} />
            </React.Fragment>
          ))}
        </div>
      );
    }
    return result;
  };

  /* Exists to support the case of a user directly entering a vendor no. and tab-blurring,
     possibly before suggestions even load.

     We utilize a timeout here since React Autosuggest changes the input value on selection,
     but takes a brief instant to do so.

     For example, the user may enter "pepsi" to load suggestions. Upon suggestion selection, the
     input box will change to the vendor no, "2000", but only AFTER the blur event is called,
     which in this example would receive "pepsi".  By utilizing a timeout, it will correctly receive
     the resolved suggestion value and detect if onSuggestionSelected already pulled the vendor,
     thereby supporting both use cases: utilizing autosuggest as well as rapid tab-blurring.
  */
  onBlur = (syntheticEvent: any) => {
    setTimeout(() => {
      const el = document.getElementById(this.id) as HTMLInputElement;

      // Prevents bomb if dom node has gone away (ex: ship to modal escape by click)
      if (!el) {
        return;
      }

      const vendorID = el.value.trim();

      // Prevents duplicative load since onSuggestionSelected creates blur event too
      if (this.lastLookup !== null && vendorID === this.lastLookup) {
        return;
      }

      this.props.onVendorLoad(vendorID);
    }, 100);
  };

  onChange = (syntheticEvent: any, event: Autosuggest.ChangeEvent) => {
    this.setState({
      value: event.newValue,
    });
  };

  // Hack to re-enable suggested vendor selection (if any) when user thinks he's cleared the field
  onKeyUp = (syntheticEvent: any) => {
    if (syntheticEvent.currentTarget.value.trim() === '') {
      this.setState({
        value: '',
      });
      this.props.onVendorLoad('');
    }
  };

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
    });
  };

  onSuggestionsFetchRequested = (req: Autosuggest.SuggestionsFetchRequestedParams) => {
    // req.reason seems to no longer exist in 9.1.x, docs wrong
    // created issue: https://github.com/moroshko/react-autosuggest/issues/386
    this.getSuggestionsDebounced(req.value);
  };

  onSuggestionSelected = (syntheticEvent: any, selection: Autosuggest.SuggestionSelectedEventData<any>) => {
    const vendorID = selection.suggestionValue;
    this.lastLookup = vendorID;
    this.props.onVendorLoad(vendorID);
  };

  /* This is used to dynamically calculate the height/width of the suggestion container.

     By default, applying a 100% width to the container would constrain it to the size of its
     containing div.  In situations (like shopkeeper) where that div is part of a flex row of
     other divs, the width would be limited significantly.  This logic allows the width to be
     calculated based on the width of the viewport (sans scroll) and "break out" of said
     containing div.

     Specifically for width, it uses all width between the left edge of the container up to 15px
     shy of the right edge of the viewport (sans scroll).

     Specifically for height, the container will always be AT LEAST 150px (minHeight) tall, and
     AT MOST (maxHeight) a height equal to the distance from the top of the container, to the
     bottom of the viewport, minus 75px (roughly size of actionbar footer in shopkeeper).
  */
  renderSuggestionsContainer = ({ containerProps, children }: any) => {
    const style: { maxHeight: string; minHeight: string; position?: string; width: string } = {
      maxHeight: '0px',
      minHeight: '0px',
      width: '0px',
    };

    if (
      containerProps.className ===
      'react-autosuggest__suggestions-container react-autosuggest__suggestions-container--open'
    ) {
      const containerRect = (document.getElementById(containerProps.id) as HTMLElement).getBoundingClientRect();
      style.minHeight = '150px';
      style.maxHeight = `${document.body.clientHeight - containerRect.top - 75}px`;
      style.width = `${document.body.clientWidth - containerRect.left - 15}px`;

      if (this.overflowBreakout) {
        style.position = 'fixed';
      }
    }

    return (
      <div {...containerProps} style={style}>
        {children}
      </div>
    );
  };

  renderSuggestion = (suggestion: IVendorLookupRecord, { query }: { query: string }) => {
    let cityState = '';
    let phone = ' ';
    cityState += suggestion.City ? suggestion.City : '';
    cityState += suggestion.State ? (cityState !== '' ? `, ${suggestion.State}` : suggestion.State) : '';

    if (suggestion.Phone) {
      phone = suggestion.Phone.replace(/\D/g, '');
    }

    const additionalNames: string[] = [];
    if (this.props.showAdditionalNames) {
      if (suggestion['Name 2']) {
        additionalNames.push(suggestion['Name 2']);
      }
      if (suggestion['Name 3'] && suggestion['Name 3'] !== suggestion['Name 2']) {
        additionalNames.push(suggestion['Name 3']);
      }
    }

    return (
      <div className="renderSuggestion">
        {this.highlight(query, suggestion.itemid)}
        {this.highlight(query, suggestion.Name, ...additionalNames)}
        {this.highlight(query, suggestion.Address)}
        {this.highlight(query, cityState)}
        {this.highlight(query, phone)}
      </div>
    );
  };

  render() {
    const { value, suggestions } = this.state;

    const inputProps = {
      disabled: this.props.disabled,
      id: this.id,
      onBlur: this.onBlur,
      onChange: this.onChange,
      onKeyUp: this.onKeyUp,
      placeholder: this.props.placeholder,
      style: this.props.style,
      value,
    };

    return (
      <Autosuggest
        getSuggestionValue={this.getSuggestionValue}
        highlightFirstSuggestion={true}
        id={this.id}
        inputProps={inputProps}
        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
        onSuggestionSelected={this.onSuggestionSelected}
        renderSuggestion={this.renderSuggestion}
        renderSuggestionsContainer={this.renderSuggestionsContainer}
        suggestions={suggestions}
      />
    );
  }
}

export default VendorLookup;
